import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { FilterMenu, type FilterMenuField } from '../filter-menu/filter-menu';
import { LoadingBar } from '../loading-bar/loading-bar';
import { Pagination } from '../pagination/pagination';
import { StatusChip } from '../status-chip/status-chip';
import {
  humanizeValue,
  scoreColor,
  scoreLabel,
  screenColor,
  STATUS_STYLES,
  typeStyle,
  urlPath,
} from '../feedback-presentation/feedback-presentation';

/** A row in the feedback table — a presentational shape mapped from a feedback item. */
export interface FeedbackRow {
  id: string;
  category: string;
  element: string;
  url: string;
  score: number | null;
  /** 'mobile' | 'tablet' | 'desktop' (or any string). */
  screen: string;
  /** Feedback status value (e.g. 'action', 'completed'). */
  status: string;
  /** Feedback type value (e.g. 'ai_feedback', 'user_feedback'). */
  type: string;
  /** ISO date string; formatted for display. */
  created: string;
  /**
   * Unread comments on this item for the current viewer. Absent or 0 shows nothing;
   * anything higher shows a badge beside the element.
   */
  unreadComments?: number;
}

/** A single sort clause: a column key and direction. */
export interface SortSpec {
  col: string;
  dir: 'asc' | 'desc';
}

/**
 * Active feedback filters. Each key is the selected value (empty/absent means
 * "all"); `score` is a band keyword (low/medium/high). Consumed server-side.
 */
export interface FeedbackFilters {
  /** Full (base) URL to filter to a single page. */
  url?: string;
  category?: string;
  screen?: string;
  status?: string;
  type?: string;
  score?: string;
  /** Author filter: an author email, or `'ai'` for AI-generated feedback. */
  user?: string;
  /** First created day to include, as `YYYY-MM-DD` (the host decides the timezone). */
  createdFrom?: string;
  /** Last created day to include, as `YYYY-MM-DD`. */
  createdTo?: string;
}

/** A select option: the value sent to the API and its display label. */
export interface FilterOption {
  value: string;
  label: string;
}

/** A table column: its key, header label, alignment, and whether it's sortable. */
interface ColumnDef {
  key: string;
  label: string;
  center?: boolean;
  sortable: boolean;
}

// `screen` is derived (no backing field) and the action column isn't sortable.
const COLUMNS: ColumnDef[] = [
  { key: 'category', label: 'Category', sortable: true },
  { key: 'element', label: 'Element', sortable: true },
  { key: 'url', label: 'URL', sortable: true },
  { key: 'score', label: 'Score', center: true, sortable: true },
  { key: 'screen', label: 'Screen', center: true, sortable: false },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'type', label: 'Type', center: true, sortable: true },
  { key: 'created', label: 'Created', sortable: true },
  { key: '_action', label: '', center: true, sortable: false },
];

/**
 * The feedback table from the design: lists feedback items in columns
 * (category, element, URL, score, screen, status, type, created) with a button
 * to open each row. Presentational — pass `items` and handle `openFeedback`.
 *
 * The panel header exposes Export / New Feedback actions via outputs; wire them
 * in the host. Pagination renders inside the panel; the host supplies
 * `page`/`perPage` (total comes from `count`) and handles `pageChange`.
 */
/** A status change made inline on a row: the affected row and the chosen status. */
export interface FeedbackRowStatusChange {
  row: FeedbackRow;
  status: string;
}

/** A status chosen for every selected row at once: the selected rows and the status. */
export interface FeedbackBulkStatusChange {
  rows: FeedbackRow[];
  status: string;
}

@Component({
  selector: 'fb-feedback-table',
  imports: [DatePipe, FilterMenu, LoadingBar, Pagination, StatusChip],
  templateUrl: './feedback-table.html',
  styleUrl: './feedback-table.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackTable {
  /** The feedback rows to display. */
  readonly items = input<FeedbackRow[]>([]);

  /**
   * True while the host is fetching a page of feedback. Shows a loading bar under
   * the column headings; with no rows yet, placeholder rows stand in for them
   * (instead of the "No feedback yet" empty state).
   */
  readonly loading = input(false);

  /** Placeholder rows shown while the first page loads. */
  protected readonly placeholderRows = [1, 2, 3, 4, 5, 6];

  /** Panel heading shown above the filter bar. */
  readonly heading = input('Feedback');

  /** Total item count — shown beside the heading and used for pagination. */
  readonly count = input(0);

  /**
   * Builds the link a row's URL opens (e.g. the main app adds the widget's activation
   * parameter). Defaults to the URL unchanged; the shown path is never affected.
   */
  readonly pageLink = input<(pageUrl: string) => string>((pageUrl) => pageUrl);

  /** Current 1-based page (controlled). */
  readonly page = input(1);

  /** Items per page; pagination is hidden when `count` fits on one page. */
  readonly perPage = input(0);

  /** Current sort (controlled); defaults to newest-created first. */
  readonly sort = input<SortSpec[]>([{ col: 'created', dir: 'desc' }]);

  /** Active filters (controlled); empty means no filtering. */
  readonly filters = input<FeedbackFilters>({});

  /**
   * Category options (value + label) for the category select. When empty, the
   * categories present in the current rows are used as a fallback. Pass the
   * full category set (e.g. the FeedbackCategory enum) for stable options under
   * server-side pagination.
   */
  readonly categoryOptions = input<FilterOption[]>([]);

  /**
   * Full (base) URLs present in the round, for the URL select. Labels are
   * rendered as the path only (the table already hides the domain).
   */
  readonly urlOptions = input<string[]>([]);

  /**
   * Author options (value + label) for the "filter by user" select. The value
   * is the author email (or `'ai'`); the label is the display name. The host
   * supplies these (e.g. from `FeedbackService.listFeedbackUsers`); when empty
   * the select is hidden.
   */
  readonly userOptions = input<FilterOption[]>([]);

  /** Available status values for each row's status chip dropdown (e.g. enum values). */
  readonly statuses = input<string[]>([]);

  /** Emitted with the row when its open button is clicked. */
  readonly openFeedback = output<FeedbackRow>();

  /** Emitted when a row's status is changed inline via its status chip. */
  readonly statusChange = output<FeedbackRowStatusChange>();

  /** Emitted with the new sort whenever the user changes sorting. */
  readonly sortChange = output<SortSpec[]>();

  /** Emitted with the full filter set whenever any filter select changes. */
  readonly filtersChange = output<FeedbackFilters>();

  /** Emitted with the requested 1-based page from the pagination control. */
  readonly pageChange = output<number>();

  /** Emitted when the New Feedback button is clicked. */
  readonly newFeedback = output<void>();

  /** Emitted when the Export button is clicked. */
  readonly exportFeedback = output<void>();

  /**
   * Shows a checkbox on each row (and a select-all for the rows in view), with a bar
   * to set one status on every selected row. Off by default.
   */
  readonly selectable = input(false);

  /** Ids of the selected rows; two-way bindable so the host can clear it (e.g. after a bulk change). */
  readonly selectedIds = model<string[]>([]);

  /** A status chosen from the bulk bar for every selected row. */
  readonly bulkStatusChange = output<FeedbackBulkStatusChange>();

  protected readonly columns = COLUMNS;

  /** The active sort entry for a column, or null. */
  protected sortEntry(col: string): { index: number; dir: 'asc' | 'desc' } | null {
    const index = this.sort().findIndex((spec) => spec.col === col);
    return index === -1 ? null : { index, dir: this.sort()[index].dir };
  }

  protected readonly isMultiSort = computed(() => this.sort().length > 1);

  protected readonly isDefaultSort = computed(() => {
    const sort = this.sort();
    return sort.length === 1 && sort[0].col === 'created' && sort[0].dir === 'desc';
  });

  /** Active sort clauses paired with their column labels (for the chips). */
  protected readonly sortChips = computed(() =>
    this.sort().map((spec) => ({
      ...spec,
      label: COLUMNS.find((column) => column.key === spec.col)?.label ?? spec.col,
    })),
  );

  /** Sortable columns not already in the sort (for the "+ Add sort" menu). */
  protected readonly addableColumns = computed(() =>
    COLUMNS.filter(
      (column) => column.sortable && !this.sort().some((spec) => spec.col === column.key),
    ),
  );

  /**
   * Plain click sets the column as the sole sort (toggling direction when it's
   * already the only one); shift-click cycles it asc → desc → off as a
   * secondary sort. Emits the resulting sort.
   */
  protected toggleSort(col: string, additive: boolean): void {
    const current = this.sort();
    const index = current.findIndex((spec) => spec.col === col);
    let next: SortSpec[];

    if (additive) {
      if (index === -1) {
        next = [...current, { col, dir: 'asc' }];
      } else if (current[index].dir === 'asc') {
        next = current.map((spec, i) => (i === index ? { col, dir: 'desc' } : spec));
      } else {
        next = current.filter((_, i) => i !== index);
      }
    } else if (index === 0 && current.length === 1) {
      next = [{ col, dir: current[0].dir === 'asc' ? 'desc' : 'asc' }];
    } else {
      next = [{ col, dir: index === -1 ? 'asc' : current[index].dir }];
    }

    this.sortChange.emit(next);
  }

  protected toggleChipDirection(col: string): void {
    this.sortChange.emit(
      this.sort().map((spec) =>
        spec.col === col ? { ...spec, dir: spec.dir === 'asc' ? 'desc' : 'asc' } : spec,
      ),
    );
  }

  protected removeSort(col: string): void {
    if (this.sort().length <= 1) {
      return;
    }
    this.sortChange.emit(this.sort().filter((spec) => spec.col !== col));
  }

  protected addSort(col: string): void {
    if (col) {
      this.sortChange.emit([...this.sort(), { col, dir: 'asc' }]);
    }
  }

  protected resetSort(): void {
    this.sortChange.emit([{ col: 'created', dir: 'desc' }]);
  }

  // Filter option lists (value sent to the API + display label).
  /** Categories: the passed-in set, or those present in the current rows. */
  protected readonly categoryFilterOptions = computed<FilterOption[]>(() => {
    const provided = this.categoryOptions();
    if (provided.length) {
      return provided;
    }
    return [...new Set(this.items().map((item) => item.category).filter(Boolean))]
      .sort()
      .map((value) => ({ value, label: humanizeValue(value) }));
  });
  private readonly singleStatusOptions: FilterOption[] = Object.entries(STATUS_STYLES).map(
    ([value, style]) => ({ value, label: style.label }),
  );

  /**
   * The status choices. A filter holding several statuses (`a,b` — e.g. from the daily
   * email's "Items for review" link) is not one of them, so it is added as its own
   * option, labeled with each status, for the select to show as selected.
   */
  protected readonly statusFilterOptions = computed<FilterOption[]>(() => {
    const activeStatus = this.filters().status ?? '';
    if (!activeStatus.includes(',')) {
      return this.singleStatusOptions;
    }

    const combinedLabel = activeStatus
      .split(',')
      .map((statusValue) => STATUS_STYLES[statusValue]?.label ?? humanizeValue(statusValue))
      .join(' + ');

    return [{ value: activeStatus, label: combinedLabel }, ...this.singleStatusOptions];
  });
  protected readonly screenFilterOptions: FilterOption[] = [
    { value: 'desktop', label: 'Desktop' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'tablet', label: 'Tablet' },
  ];
  protected readonly typeFilterOptions: FilterOption[] = [
    { value: 'user_feedback', label: 'User' },
    { value: 'ai_feedback', label: 'AI' },
  ];
  protected readonly scoreFilterOptions: FilterOption[] = [
    { value: 'high', label: 'High (7–10)' },
    { value: 'medium', label: 'Medium (4–6.9)' },
    { value: 'low', label: 'Low (0–3.9)' },
  ];

  /** The filters offered in the Filters menu, in menu order; User only when the round has authors. */
  protected readonly filterFields = computed<FilterMenuField[]>(() => {
    const menuFields: FilterMenuField[] = [
      { kind: 'select', key: 'url', label: 'URL', options: this.urlOptions().map((url) => ({ value: url, label: urlPath(url) })) },
      { kind: 'select', key: 'category', label: 'Category', options: this.categoryFilterOptions() },
      { kind: 'select', key: 'status', label: 'Status', options: this.statusFilterOptions() },
      { kind: 'select', key: 'screen', label: 'Screen', options: this.screenFilterOptions },
      { kind: 'select', key: 'type', label: 'Type', options: this.typeFilterOptions },
    ];
    if (this.userOptions().length) {
      menuFields.push({ kind: 'select', key: 'user', label: 'User', options: this.userOptions() });
    }
    menuFields.push(
      { kind: 'select', key: 'score', label: 'Score', options: this.scoreFilterOptions },
      { kind: 'dateRange', key: 'created', label: 'Created', fromKey: 'createdFrom', toKey: 'createdTo' },
    );
    return menuFields;
  });

  /** The filters as the menu's key → value map (an interface isn't assignable to one directly). */
  protected readonly filterValues = computed<Record<string, string | undefined>>(() => ({ ...this.filters() }));

  /** The Filters menu hands back the whole new set (every key is a FeedbackFilters one). */
  protected onFiltersApplied(nextFilters: Record<string, string>): void {
    this.filtersChange.emit(nextFilters as FeedbackFilters);
  }

  /** The selected rows that are in view (a selection never reaches rows off this page). */
  protected readonly selectedRows = computed(() => {
    const selectedIdSet = new Set(this.selectedIds());
    return this.items().filter((row) => selectedIdSet.has(row.id));
  });

  protected readonly allRowsSelected = computed(
    () => this.items().length > 0 && this.selectedRows().length === this.items().length,
  );

  protected readonly someRowsSelected = computed(
    () => this.selectedRows().length > 0 && !this.allRowsSelected(),
  );

  /** The bulk bar's status choices: the host's statuses, else every known one. */
  protected readonly bulkStatusOptions = computed<FilterOption[]>(() => {
    const statusValues = this.statuses().length ? this.statuses() : Object.keys(STATUS_STYLES);
    return statusValues.map((statusValue) => ({
      value: statusValue,
      label: STATUS_STYLES[statusValue]?.label ?? humanizeValue(statusValue),
    }));
  });

  protected isRowSelected(rowId: string): boolean {
    return this.selectedIds().includes(rowId);
  }

  protected toggleRowSelection(rowId: string, selected: boolean): void {
    const otherIds = this.selectedIds().filter((selectedId) => selectedId !== rowId);
    this.selectedIds.set(selected ? [...otherIds, rowId] : otherIds);
  }

  /** Selects every row in view, or clears the selection. */
  protected toggleAllRows(selected: boolean): void {
    this.selectedIds.set(selected ? this.items().map((row) => row.id) : []);
  }

  protected clearSelection(): void {
    this.selectedIds.set([]);
  }

  protected onBulkStatus(status: string): void {
    const rows = this.selectedRows();
    if (status && rows.length) {
      this.bulkStatusChange.emit({ rows, status });
    }
  }

  // Presentation helpers shared with the detail drawer (see feedback-presentation).
  protected readonly humanize = humanizeValue;
  protected readonly typeStyle = typeStyle;
  protected readonly screenColor = screenColor;
  protected readonly scoreLabel = scoreLabel;
  protected readonly scoreColor = scoreColor;
  protected readonly urlPath = urlPath;

  /** Only http(s) URLs get the open-in-new-window link, never a `javascript:` or relative one. */
  protected isWebUrl(url: string): boolean {
    return /^https?:\/\//i.test(url ?? '');
  }
}
